// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { AmazonConnectChatSettings } from "@eisinc/App/Components/XngScriptInjector/utils";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import logger from "@insite/client-framework/Logger";
import { fetch } from "@insite/client-framework/ServerSideRendering";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, DispatchProp } from "react-redux";
import { css } from "styled-components";

/* eslint-disable camelcase */
declare let amazon_connect: any;

const enum fields {}

const mapStateToProps = (state: any) => {
    const session = getSession(state);
    const { amazonConnectChatSettings } = getSettingsCollection(state);

    return {
        firstName: session?.firstName,
        lastName: session?.lastName,
        email: session?.email,
        isAuthenticated: session?.isAuthenticated,
        amazonConnectChatSettings: amazonConnectChatSettings as AmazonConnectChatSettings,
    };
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    DispatchProp<any> & {
        fields: {};
    };

interface AwsChatStyles {
    container?: InjectableCss;
}

const awsChatStyles: AwsChatStyles = {
    container: {
        css: css`
            background: #e5e5e5;
            padding: 10px;
        `,
    },
};

const styles = awsChatStyles;
const AWSChat: React.FC<Props> = ({ amazonConnectChatSettings, fields: { mixPanelApiKey } }) => {
    const { isInShell } = useShellContext();
    const [isScriptAttached, setIsScriptAttached] = React.useState(false);

    const attachScriptToDocument = () => {
        setIsScriptAttached(true);

        const script = document.createElement("script");
        script.innerHTML = `
            (function (w, d, x, id) {
                s = d.createElement('script');
                s.src = '${amazonConnectChatSettings.amazonChatSource}';
                s.async = 1;
                s.id = id;
                d.getElementsByTagName('head')[0].appendChild(s);
                w[x] = w[x] || function () { (w[x].ac = w[x].ac || []).push(arguments) };
            })(window, document, 'amazon_connect', '${amazonConnectChatSettings.widgetId}');
            
            amazon_connect('styles', { iconType: 'CHAT_VOICE', openChat: { color: '${amazonConnectChatSettings.iconColor}', backgroundColor: '${amazonConnectChatSettings.iconBackgroundColor}' }, closeChat: { color: '${amazonConnectChatSettings.iconColor}', backgroundColor: '${amazonConnectChatSettings.iconBackgroundColor}' } });
            amazon_connect('snippetId', '${amazonConnectChatSettings.snippetId}');
            amazon_connect('supportedMessagingContentTypes', ['text/plain', 'text/markdown', 'application/vnd.amazonaws.connect.message.interactive', 'application/vnd.amazonaws.connect.message.interactive.response']);
        `;

        document.head.appendChild(script);
    };

    React.useEffect(() => {
        if (amazonConnectChatSettings?.amazonChatSource && amazonConnectChatSettings?.snippetId && !isScriptAttached) {
            attachScriptToDocument();
        }
    }, [amazonConnectChatSettings]);

    React.useEffect(() => {
        if (isScriptAttached && typeof amazon_connect !== "undefined") {
            amazon_connect("authenticate", function (callback: any) {
                fetch(`/api/custom/amazonChat/token?currentPage=${window?.location?.pathname}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                })
                    .then(response => response.json())
                    .then(data => {
                        callback(data.data);
                    })
                    .catch(error => {
                        logger.error(`Error fetching token: ${error}`);
                    });
            });
        }
    }, [isScriptAttached]);

    if (isInShell) {
        return (
            <StyledWrapper {...styles.container} data-test-selectore="SearchEngineOptimization">
                {translate("AWS Chat Widget")} v.2021.0.0
            </StyledWrapper>
        );
    }

    return <></>;
};

const definition: WidgetDefinition = {
    group: "Common",
    displayName: "AWS Chat Widget",
    allowedContexts: ["Header"],
    fieldDefinitions: [],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(AWSChat),
    definition,
};

export default widgetModule;
